import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { X, Play, Sparkle, Copy, CheckCircle, Spinner, FilmSlate, Lightning, Lightbulb, ArrowClockwise, VideoCamera, Link, ArrowSquareOut, Info, CaretDown, TextAlignLeft, TextT, Megaphone, Microphone, Package, Funnel } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { contentAPI, growthAPI, videoAPI, aiAPI } from '@/utils/api';
import { toast } from 'sonner';
import { WhatsNextContent } from '@/components/WhatsNext';
import WhatNextCard from '@/components/WhatNextCard';
import FreeLimitModal from '@/components/FreeLimitModal';
import OutputActions from '@/components/OutputActions';
import api from '@/utils/api';
import { useFormDefaults } from '@/store/usePayGBotContext';
import { useMissionHandoff } from '@/store/missionHandoffContext';
import { normalizeBuilderAsset } from '@/utils/normalizeBuilderAsset';
import { useWorkflowProgress } from '@/store/workflowProgress';
import { useWorkflowStore } from '@/store/workflowStore';
import ToolLinks from './ToolLinks';
import BusinessMemoryBanner from './BusinessMemoryBanner';
import NextStepGuide from './NextStepGuide';
import EditablePreview from './EditablePreview';

// Inline-editable text field used by the Review & Edit step. Shows a live
// character counter (turns red when over the cap) and a colour-coded border
// so an over-limit field can never be accidentally submitted.
const EditableField = ({ testid, label, value, onChange, max, rows = 2, placeholder = '' }) => {
  const length = (value || '').length;
  const over = length > max;
  return (
    <div data-testid={`${testid}-wrapper`}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-violet-300 uppercase tracking-wider">{label}</label>
        <span
          data-testid={`${testid}-counter`}
          className={`text-[11px] tabular-nums ${over ? 'text-rose-400 font-semibold' : 'text-slate-500'}`}
        >
          {length} / {max}
        </span>
      </div>
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className={`w-full p-3 rounded-lg bg-black/30 border text-slate-200 text-sm leading-relaxed resize-y focus:outline-none focus:ring-1 ${
          over
            ? 'border-rose-500/60 focus:ring-rose-500/40'
            : 'border-white/10 focus:ring-violet-500/40 focus:border-violet-500/50'
        }`}
      />
      {over && (
        <p className="mt-1 text-[11px] text-rose-400" data-testid={`${testid}-over-limit`}>
          Over the {max}-character limit. Trim {length - max} character{length - max === 1 ? '' : 's'} to continue.
        </p>
      )}
    </div>
  );
};

const GenerateContentModal = ({ isOpen, onClose, initialTopic = '', selectedIdea = null, onSuccess, onAction, userPlan = 'free', onPickStep, onFindViralIdeas, onPlanMyWeek }) => {
  // Get global business context for auto-fill
  const formDefaults = useFormDefaults();
  const workflowProgress = useWorkflowProgress();
  const setContentCreated = useWorkflowStore((state) => state.setContentCreated);
  // Round 108 — mission handoff. When the customer arrives via
  // Approve & Generate → strategist → Create This Content, the
  // dashboard populates `missionHandoff` with the approved edits +
  // strategist recommendations. We use these as the authoritative
  // source for topic / platform / tone and short-circuit the
  // "What topic should I create content about?" prompt.
  const { handoff: missionHandoff, pickField: pickMissionField } = useMissionHandoff();
  const isMissionMode = !!(missionHandoff && missionHandoff.asset_id);
  
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [topic, setTopic] = useState(initialTopic);
  const [result, setResult] = useState(null);
  const [streamingResult, setStreamingResult] = useState({});
  const [currentStage, setCurrentStage] = useState('');
  const [stageMessage, setStageMessage] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [showWhatsNext, setShowWhatsNext] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const abortControllerRef = useRef(null);
  
  // Form fields with defaults from business context
  const [platform, setPlatform] = useState(formDefaults.platform || 'tiktok');
  const [style, setStyle] = useState(formDefaults.style || 'educational');
  const [tone, setTone] = useState(formDefaults.tone || 'friendly');
  
  // Video usage state (for unlimited badge)
  const [videoUsage, setVideoUsage] = useState(null);
  
  // Video generation state
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null); // null, 'queued', 'processing', 'completed', 'failed'
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoError, setVideoError] = useState(null);
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  
  // Free tier limit state
  const [showFreeLimitModal, setShowFreeLimitModal] = useState(false);
  const [freeLimitPrompt, setFreeLimitPrompt] = useState(null);
  const [whatNextData, setWhatNextData] = useState(null);
  const [generationLimit, setGenerationLimit] = useState(null);
  
  // Execution instructions toggle
  const [showExecutionDetails, setShowExecutionDetails] = useState(false);
  
  // PART 8: Selected hook state - allows user to change hook selection
  const [selectedHook, setSelectedHook] = useState(null);
  
  // PART 3 & 4: Video credits state
  const [videoCredits, setVideoCredits] = useState(0);
  // Round 58 (2026-05-22) — founder/admin/internal account flag.
  // Seeded synchronously from localStorage so the credit-gate check
  // never fires for an internal account, even before the async
  // `/video-credits/balance` fetch completes. Refreshed from the
  // backend response once that fetch lands.
  const [isInternalAccount, setIsInternalAccount] = useState(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      const u = JSON.parse(raw);
      return !!(u?.is_internal || u?.plan_tier === 'internal' || u?.role === 'admin');
    } catch (_e) {
      return false;
    }
  });

  // Review-and-edit state. These are ALWAYS the values sent to Create Video,
  // not the raw `result.*` fields. Clients can edit prompt/script/cta/scene
  // before any Runway or ElevenLabs call is made. The fields reset whenever a
  // new generation result lands.
  const [videoPromptDraft, setVideoPromptDraft] = useState('');

  // Phase 4.1 — Video Engine v2 founder-only toggle. Defaults OFF so the
  // legacy pipeline keeps running unchanged. Can be flipped from the
  // UI (founder only) or pre-armed via `?engine=v2` query param so it
  // survives a page reload during canary testing. Backend still
  // enforces founder + VIDEO_ENGINE_V2_REAL_PROVIDERS=1, so flipping
  // this for a client account just gets a clean 403 / 409.
  const [useV2Engine, setUseV2Engine] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('engine') === 'v2';
    } catch (_e) {
      return false;
    }
  });

  // v2 env-flag status from the backend. `null` = not yet fetched.
  // We surface this in the founder diagnostic strip below the engine
  // selector so it's instantly obvious whether a canary will actually
  // run, or whether `VIDEO_ENGINE_V2_REAL_PROVIDERS=1` is still needed.
  const [v2EnvStatus, setV2EnvStatus] = useState(null);
  const [v2EnvLoading, setV2EnvLoading] = useState(false);
  const [v2EnvError, setV2EnvError] = useState(null);

  // Pull the v2 status exactly once when the modal opens, but only for
  // internal accounts (the endpoint 403s otherwise so non-founders
  // would just see an error). If the user logs in mid-session and
  // `isInternalAccount` flips true, we re-fetch.
  useEffect(() => {
    if (!isInternalAccount) return;
    let cancelled = false;
    setV2EnvLoading(true);
    setV2EnvError(null);
    api
      .get('/v2-demo/status')
      .then((r) => { if (!cancelled) setV2EnvStatus(r?.data || null); })
      .catch((e) => {
        if (!cancelled) {
          setV2EnvError(e?.response?.status || 'unknown');
          setV2EnvStatus(null);
        }
      })
      .finally(() => { if (!cancelled) setV2EnvLoading(false); });
    return () => { cancelled = true; };
  }, [isInternalAccount]);

  const v2EnvEnabled = !!v2EnvStatus?.real_providers_available;

  // If `?engine=v2` was passed but the backend env flag is OFF, fall
  // back to legacy and explain why — better than submitting and
  // getting a backend 409 mid-flow.
  useEffect(() => {
    if (
      useV2Engine
      && v2EnvStatus !== null
      && !v2EnvLoading
      && !v2EnvEnabled
    ) {
      setUseV2Engine(false);
      toast.error(
        '?engine=v2 was set, but VIDEO_ENGINE_V2_REAL_PROVIDERS is OFF on the backend. Falling back to legacy.',
        { duration: 6000 }
      );
    }
  }, [useV2Engine, v2EnvStatus, v2EnvLoading, v2EnvEnabled]);
  const [scriptDraft, setScriptDraft] = useState('');
  const [shorteningScript, setShorteningScript] = useState(false);
  const [ctaDraft, setCtaDraft] = useState('');
  const [sceneDescriptionDraft, setSceneDescriptionDraft] = useState('');
  const PROMPT_MAX = 990;
  const SCRIPT_MAX = 1500;
  const CTA_MAX = 150;
  const SCENE_MAX = 990;
  // Round 52.1 (2026-05-19) — default PayGBot-generated voiceover script
  // must be 25–32 words target, 35 hard max (≈ 10–15 s of ElevenLabs audio).
  // The backend also auto-shortens AFTER measuring actual audio if it
  // overshoots 15 s — but enforcing the cap here keeps the user from
  // submitting a doomed script in the first place. Scripts over 35 are
  // auto-shortened on submit and BLOCKED from entering the video queue
  // unless the user is in the paid extended tier (handled server-side).
  const SCRIPT_WORD_MIN = 25;
  const SCRIPT_WORD_MAX = 35;
  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };
  const truncateToMaxWords = (text, maxWords = SCRIPT_WORD_MAX) => {
    if (!text) return '';
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) return text.trim();
    const kept = words.slice(0, maxWords).join(' ');
    // Trim back to the last sentence boundary so the cut doesn't leave
    // a hanging clause; if no boundary in the kept window, add a period.
    const lastPunct = Math.max(kept.lastIndexOf('.'), kept.lastIndexOf('!'), kept.lastIndexOf('?'));
    if (lastPunct > kept.length * 0.5) return kept.slice(0, lastPunct + 1);
    return kept.replace(/[,;:\-\s]+$/, '') + '.';
  };
  const scriptWordCount = countWords(scriptDraft);
  const scriptWordsOverCap = scriptWordCount > SCRIPT_WORD_MAX;
  const scriptWordsUnderMin = scriptWordCount > 0 && scriptWordCount < SCRIPT_WORD_MIN;

  // Voiceover preview (no Runway call).
  // `previewedScript` records the exact script text that produced the
  // currently-loaded preview audio. If the user edits the script after a
  // preview, the UI shows "Script changed — preview again" so users don't
  // create a real video from outdated audio.
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const [previewDuration, setPreviewDuration] = useState(null);
  const [previewedScript, setPreviewedScript] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  // `previewPlaybackError` goes true when the browser <audio> element fires
  // an `error` event (e.g. 404, CORS, unsupported codec, network fail). When
  // set, the "ready" pill is suppressed and the UI shows a clear failure.
  const [previewPlaybackError, setPreviewPlaybackError] = useState(false);

  // ──────────────────────────────────────────────────────────
  // Video duration / pricing preflight (P0 wire-up 2026-05-01)
  //
  // Debounced: when the user edits `scriptDraft`, we call the pure
  // quote endpoint POST /api/videos/preflight/estimate — no DB
  // writes, no provider calls. The response gives us tier /
  // word_count / estimated_seconds / price_display / upcharge_required.
  // The UI surfaces this BEFORE the user can click Create Video, and
  // if the tier is `extended`, a confirmation checkbox must be ticked
  // before Create Video is enabled. If `blocked`, Create Video stays
  // disabled with the server's own BLOCKED_MESSAGE.
  // ──────────────────────────────────────────────────────────
  const [preflightData, setPreflightData] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState(null);
  const [extendedUpchargeConfirmed, setExtendedUpchargeConfirmed] = useState(false);

  // When a new result arrives, refresh the editable drafts. Safe trim so we
  // never start with leading/trailing whitespace, and clip overlong values
  // to the field cap so the textarea isn't already in an error state.
  useEffect(() => {
    if (!result) {
      setVideoPromptDraft('');
      setScriptDraft('');
      setCtaDraft('');
      setSceneDescriptionDraft('');
      setPreviewAudioUrl(null);
      setPreviewedScript(null);
      setPreviewDuration(null);
      setPreviewError(null);
      setPreviewPlaybackError(false);
      setPreflightData(null);
      setPreflightError(null);
      setExtendedUpchargeConfirmed(false);
      return;
    }
    setVideoPromptDraft(((result.video_prompt || '').trim()).slice(0, PROMPT_MAX));
    setScriptDraft(((result.script || '').trim()).slice(0, SCRIPT_MAX));
    setCtaDraft(((result.cta || '').trim()).slice(0, CTA_MAX));
    setPreviewAudioUrl(null);
    setPreviewedScript(null);
    setPreviewDuration(null);
    setPreviewError(null);
    setPreviewPlaybackError(false);
    // Reset preflight so stale tier/price from the previous asset never
    // silently applies to a fresh generation.
    setPreflightData(null);
    setPreflightError(null);
    setExtendedUpchargeConfirmed(false);
    // Scene description = the hook (a 1-2 sentence scene-setter). Falls back
    // to the first sentence of video_prompt if the model didn't produce one.
    const sceneFromHook = (result.hook || '').trim();
    const sceneFallback = (result.video_prompt || '').split(/[.!?]/)[0].trim();
    setSceneDescriptionDraft((sceneFromHook || sceneFallback).slice(0, SCENE_MAX));
  }, [result]);

  // Debounced preflight quote. Re-runs 400 ms after the user stops typing
  // in scriptDraft. When the script changes tier (e.g. standard → extended
  // after a few extra words), we clear `extendedUpchargeConfirmed` so the
  // user must re-confirm the new +$2 upcharge instead of it carrying over
  // silently. Pure-quote endpoint — never touches Runway/ElevenLabs/DB.
  useEffect(() => {
    const clean = (scriptDraft || '').trim();
    if (!clean) {
      setPreflightData(null);
      setPreflightError(null);
      setPreflightLoading(false);
      return undefined;
    }
    setPreflightLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await videoAPI.estimatePreflight({ script: clean });
        setPreflightData(data);
        setPreflightError(null);
        // Reset upcharge confirmation if the tier changed from what the
        // user previously confirmed. Strict: we ONLY preserve the
        // confirmation if the tier is still extended — any other shift
        // (standard / blocked) invalidates it.
        setExtendedUpchargeConfirmed((prev) => (prev && data?.tier === 'extended' ? true : false));
      } catch (err) {
        // Don't block the user on a transient failure — the server-side
        // guards in /api/videos/create will still 400/402 on bad input.
        setPreflightError(err?.response?.data?.detail || 'Couldn\'t estimate duration. Try again.');
        setPreflightData(null);
      } finally {
        setPreflightLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [scriptDraft]);

  // Voiceover preview handler — calls ElevenLabs only, NEVER Runway.
  const handleVoiceoverPreview = async () => {
    const cleanScript = (scriptDraft || '').trim();
    if (!cleanScript) {
      toast.error('Voiceover script is required to generate a preview.');
      return;
    }
    if (cleanScript.length > SCRIPT_MAX) {
      toast.error(`Script is over the ${SCRIPT_MAX}-character limit.`);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewPlaybackError(false);
    try {
      const response = await videoAPI.previewVoiceover({ script: cleanScript });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const url = response.audio_url.startsWith('http')
        ? response.audio_url
        : `${baseUrl}${response.audio_url}`;
      setPreviewAudioUrl(url);
      setPreviewDuration(response.audio_duration);

      // Round 84 — If the backend rewrote the script sentence-aware to
      // fit under 15s, update the editable script field + previewedScript
      // so the UI reflects what was actually voiced. No more "script in
      // editor doesn't match what I just heard played back".
      const scriptToTrack =
        response.script_was_shortened && response.final_script
          ? response.final_script
          : cleanScript;
      if (response.script_was_shortened && response.final_script) {
        setScriptDraft(response.final_script);
      }
      setPreviewedScript(scriptToTrack);
      setPreviewPlaybackError(false);
      toast.success(
        response.script_was_shortened
          ? `Script shortened to fit 15s — ${response.audio_duration?.toFixed?.(1) || '?'}s preview ready.`
          : `Voiceover preview loaded (${response.audio_duration?.toFixed?.(1) || '?'}s). Press play to listen.`,
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Voiceover preview failed. Please check voice settings or try again.';
      setPreviewError(msg);
      toast.error(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Update form defaults when business context changes
  useEffect(() => {
    if (formDefaults.platform) setPlatform(formDefaults.platform);
    if (formDefaults.style) setStyle(formDefaults.style);
    if (formDefaults.tone) setTone(formDefaults.tone);
  }, [formDefaults.platform, formDefaults.style, formDefaults.tone]);

  // Streaming progress stages
  const stages = [
    { key: 'starting', label: 'Initializing AI', icon: '🚀' },
    { key: 'analyzing', label: 'Analyzing topic', icon: '🔍' },
    { key: 'hooks', label: 'Generating hooks', icon: '✨' },
    { key: 'script', label: 'Building script', icon: '📝' },
    { key: 'processing', label: 'Processing results', icon: '⚙️' },
    { key: 'caption', label: 'Writing caption', icon: '💬' },
    { key: 'hashtags', label: 'Adding hashtags', icon: '#️⃣' },
  ];

  // Fetch content suggestions and video usage when modal opens
  useEffect(() => {
    if (isOpen) {
      if (step === 'input' && !initialTopic) {
        fetchSuggestions();
      }
      fetchVideoUsage();
      fetchVideoCredits();
      fetchGenerationLimit();
    }
  }, [isOpen]);

  const fetchGenerationLimit = async () => {
    try {
      const response = await aiAPI.getGenerationLimit('content_generation');
      setGenerationLimit(response.data);
    } catch (error) {
      console.error('Failed to fetch generation limit:', error);
    }
  };

  // Update topic when initialTopic changes
  useEffect(() => {
    if (initialTopic) {
      setTopic(initialTopic);
    }
  }, [initialTopic]);

  // Round 108 — when arriving via a mission handoff, prefill the
  // topic / platform / tone from the customer's approved edits +
  // strategist recommendations. Precedence: approved_edits >
  // strategist_outputs > asset_payload > business_context (via
  // pickMissionField). Only fires once per open + handoff.
  useEffect(() => {
    if (!isOpen || !isMissionMode) return;
    const missionTopic = pickMissionField('title', 'hook', 'caption')
      || (missionHandoff?.strategist_outputs?.hooks
          && missionHandoff.strategist_outputs.hooks[0])
      || missionHandoff?.mission_title
      || '';
    if (missionTopic) setTopic(missionTopic);
    const missionPlatform = pickMissionField('platform', 'preferred_platform', 'recommended_platform');
    if (missionPlatform && typeof missionPlatform === 'string') {
      setPlatform(missionPlatform.toLowerCase());
    }
    const missionTone = pickMissionField('tone', 'brand_voice');
    if (missionTone && typeof missionTone === 'string') {
      setTone(missionTone.toLowerCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMissionMode]);

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await growthAPI.getContentSuggestions();
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const fetchVideoUsage = async () => {
    try {
      const response = await videoAPI.getUsage();
      setVideoUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch video usage:', error);
    }
  };
  
  // PART 3: Fetch video credit balance
  // Round 58 (2026-05-22) — also track `is_internal` from the credits
  // endpoint so the credit-gate check below cannot fire for founder/
  // admin accounts. The backend already reports `is_internal: true`
  // for these users (with video_credits=99999); we capture both.
  const fetchVideoCredits = async () => {
    try {
      const response = await videoAPI.getCredits();
      const balance = response.data?.video_credits ?? 0;
      const internal = response.data?.is_internal === true;
      setVideoCredits(balance);
      setIsInternalAccount(internal);
      console.info(
        `[VideoCredits] balance=${balance} is_internal=${internal} `
        + `can_create_video=${response.data?.can_create_video} source=/video-credits/balance`
      );
    } catch (error) {
      console.error('Failed to fetch video credits:', error);
    }
  };

  const getCurrentStageIndex = () => {
    return stages.findIndex(s => s.key === currentStage);
  };

  const handleGenerateWithStreaming = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    if (isGenerating) {
      toast.info('Already generating content...');
      return;
    }

    // Check generation limit for free users
    if (generationLimit && !generationLimit.can_generate) {
      try {
        const promptResponse = await aiAPI.getFreeLimitPrompt('content_generation');
        setFreeLimitPrompt(promptResponse.data);
        setShowFreeLimitModal(true);
      } catch (error) {
        toast.error(generationLimit.message || 'Generation limit reached');
      }
      return;
    }

    setIsGenerating(true);
    setStep('generating');
    setStreamingResult({});
    setCurrentStage('starting');
    setStageMessage('Initializing AI...');

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      const response = await contentAPI.generateStream({ topic });
      
      if (!response.ok) {
        // Check if this is a free tier limit error
        if (response.status === 429) {
          const errorData = await response.json();
          if (errorData.detail?.limit_reached) {
            setFreeLimitPrompt(errorData.detail.upgrade_prompt);
            setShowFreeLimitModal(true);
            setStep('input');
            setIsGenerating(false);
            return;
          }
        }
        throw new Error('Failed to start streaming');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            continue;
          }
          
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.stage) {
                setCurrentStage(data.stage);
                setStageMessage(data.message || '');
              } else if (data.field) {
                setStreamingResult(prev => ({
                  ...prev,
                  [data.field]: data.value
                }));
              } else if (data.title && data.script) {
                setResult(data);
                setStreamingResult(data);
                setCurrentStage('complete');
                
                // Video is now prompt-based (external tools) - no internal video status tracking needed
                
                // Fetch What Next guidance
                try {
                  const whatNextResponse = await aiAPI.getWhatNext('content_generation');
                  setWhatNextData(whatNextResponse.data);
                  
                  // Refresh generation limit
                  fetchGenerationLimit();
                } catch (e) {
                  console.error('Failed to fetch what next:', e);
                }
                
                setTimeout(() => {
                  setStep('result');
                  setIsGenerating(false);
                  setContentCreated(true); // Mark content as created for Business Completion State
                  // Round 110 (2026-02-14) — forward the complete
                  // builder output as a normalized mission asset
                  // so /finalize can persist it verbatim onto
                  // Today's Mission. `data` is the full streaming
                  // response (title, selected_hook, script, caption,
                  // hashtags, video_prompt, ...). Any new fields
                  // added in the future (Runway URL, ElevenLabs
                  // voice_id, transcripts, thumbnails, SEO, …) flow
                  // through without editing this line.
                  if (onSuccess) {
                    onSuccess({
                      meta: normalizeBuilderAsset(data, { platform }),
                    });
                  }
                }, 500);
              } else if (data.message && !data.stage) {
                throw new Error(data.message);
              }
            } catch (parseError) {
              if (parseError.message && !parseError.message.includes('JSON')) {
                throw parseError;
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      
      if (error.name === 'AbortError') {
        toast.info('Generation cancelled');
      } else {
        toast.error(error.message || 'Failed to generate content');
      }
      
      setStep('input');
      setIsGenerating(false);
      setCurrentStage('');
    }
  };

  const handleRegenerate = async () => {
    if (isGenerating) return;
    
    setIsGenerating(true);
    setStep('generating');
    setStreamingResult({});
    setCurrentStage('starting');
    
    try {
      const response = await contentAPI.generate({ topic, regenerate: true });
      
      setResult(response.data);
      setStreamingResult(response.data);
      setCurrentStage('complete');
      
      setTimeout(() => {
        setStep('result');
        setIsGenerating(false);
        toast.success('New content generated!');
      }, 300);
    } catch (error) {
      setIsGenerating(false);
      toast.error(error.response?.data?.detail || 'Failed to regenerate content');
      setStep('result');
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  // PART 8: Handle hook selection - updates the selected hook used for video generation
  const handleSelectHook = (hook) => {
    setSelectedHook(hook);
    // Update result to reflect the new selection
    if (result) {
      setResult(prev => ({
        ...prev,
        selected_hook: hook
      }));
    }
    toast.success('Hook selected!');
  };
  
  // Get the effective selected hook (user selection or default from result)
  const getEffectiveHook = () => {
    return selectedHook || result?.selected_hook;
  };

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setStep('input');
    setTopic(initialTopic);
    setResult(null);
    setStreamingResult({});
    setCurrentStage('');
    setStageMessage('');
    setShowWhatsNext(false);
    setIsGenerating(false);
    // Reset video state
    setVideoJobId(null);
    setVideoStatus(null);
    setVideoUrl(null);
    setVideoError(null);
    setIsCreatingVideo(false);
    // PART 8: Reset selected hook
    setSelectedHook(null);
    onClose();
  };

  const handleNewContent = () => {
    setStep('input');
    setTopic('');
    setResult(null);
    setStreamingResult({});
    setCurrentStage('');
    setShowWhatsNext(false);
    setIsGenerating(false);
    // Reset video state
    setVideoJobId(null);
    setVideoStatus(null);
    setVideoUrl(null);
    setVideoError(null);
    setIsCreatingVideo(false);
    // PART 8: Reset selected hook
    setSelectedHook(null);
  };

  const handleWhatsNextAction = (action) => {
    if (onAction) {
      onAction(action);
    }
    handleClose();
  };

  // ──────────────────────────────────────────────────────────
  // Universal Editable Preview Mode (Phase 2) — Captions / Scripts
  //
  // PATCH /api/content/{id}. Server whitelist only allows the
  // GENERATED_TEXT_EDITABLE_FIELDS. business_memory is NEVER
  // touched. This is independent of the existing video Review &
  // Edit drafts (scriptDraft / videoPromptDraft / etc) which are
  // for in-flight Runway preview.
  // ──────────────────────────────────────────────────────────
  const [savingContentEdits, setSavingContentEdits] = useState(false);
  const editableContent = useMemo(() => ({
    title: result?.title || '',
    selected_hook: result?.selected_hook || '',
    script: result?.script || '',
    caption: result?.caption || '',
    hashtags: result?.hashtags || [],
  }), [result]);

  const handleSaveContentEdits = useCallback(async (next) => {
    if (!result?.id) {
      toast.error('Content ID missing — please regenerate.');
      return;
    }
    setSavingContentEdits(true);
    try {
      const body = {
        title: next.title,
        selected_hook: next.selected_hook,
        script: next.script,
        caption: next.caption,
        hashtags: next.hashtags,
      };
      const { data } = await api.patch(`/content/${result.id}`, body, { timeout: 30000 });
      setResult((prev) => (prev ? { ...prev, ...body, last_edited_at: data?.saved_at } : prev));
    } finally {
      setSavingContentEdits(false);
    }
  }, [result?.id]);

  // Video creation handler
  const handleCreateVideo = async () => {
    // Trim-aware validation — whitespace-only fields are blocked at the
    // earliest possible moment so we never queue a job that would fail
    // server-side and waste a Runway credit.
    let cleanScript = (scriptDraft || '').trim();
    const cleanPrompt = (videoPromptDraft || '').trim();
    const cleanCta = (ctaDraft || '').trim();
    const cleanScene = (sceneDescriptionDraft || '').trim();

    if (!cleanScript) {
      toast.error('Voiceover script is required before creating a video');
      return;
    }
    if (cleanScript.length > SCRIPT_MAX) {
      toast.error(`Voiceover script is over the ${SCRIPT_MAX}-character limit`);
      return;
    }
    // Round 52 — hard 25–38 word cap. Over 38 → SENTENCE-AWARE rewrite
    // before submit (Round 85: was naive truncate that lost the CTA).
    // Under 25 → block (audio would be < 10 s and feel rushed).
    const wcBefore = countWords(cleanScript);
    if (wcBefore > SCRIPT_WORD_MAX) {
      try {
        const resp = await videoAPI.shortenScript({
          script: cleanScript,
          title: result?.title || topic || 'PayGBot short video',
        });
        setScriptDraft(resp.shortened_script);
        // Clear stale voice preview since script changed.
        setPreviewedScript(null);
        setPreviewAudioUrl(null);
        setPreviewDuration(null);
        toast.warning(
          `Script rewritten ${wcBefore} → ${resp.shortened_words} words (hook + insight + CTA preserved). Click Create Video again to confirm.`,
        );
      } catch (err) {
        toast.error('Could not rewrite script. Please edit it manually under the cap.');
      }
      return;
    }
    if (wcBefore < SCRIPT_WORD_MIN) {
      toast.error(
        `Script must be at least ${SCRIPT_WORD_MIN} words (≈ 10 s). Currently ${wcBefore}.`
      );
      return;
    }
    if (!cleanPrompt) {
      toast.error('Video prompt required before creating video');
      return;
    }
    if (cleanPrompt.length < 10) {
      toast.error('Video prompt must be at least 10 characters describing the scene');
      return;
    }
    if (cleanPrompt.length > PROMPT_MAX) {
      toast.error(`Video prompt is over the ${PROMPT_MAX}-character limit`);
      return;
    }
    if (cleanCta.length > CTA_MAX) {
      toast.error(`Call to action is over the ${CTA_MAX}-character limit`);
      return;
    }
    if (cleanScene.length > SCENE_MAX) {
      toast.error(`Scene description is over the ${SCENE_MAX}-character limit`);
      return;
    }

    // Preflight gate — mirrors the server-side 400/402 guards so the user
    // sees an honest rejection BEFORE we spend a second debit of video
    // credits. If the preflight hasn't returned yet, we still attempt the
    // create: the server is the source of truth and will 400/402 if needed.
    if (preflightData) {
      if (preflightData.tier === 'blocked' || preflightData.allowed === false) {
        toast.error(preflightData.message || 'This script is too long for PayGBot DFY video. Please shorten it or split it into multiple videos.');
        return;
      }
      if (preflightData.tier === 'extended' && !extendedUpchargeConfirmed) {
        toast.error('Please confirm the +$2 upcharge for extended videos before continuing.');
        return;
      }
    }

    // PART 4: Check video credits before starting generation
    // Round 58 — founder/admin/internal accounts bypass the credit gate
    // entirely. The backend also bypasses (see `should_bypass_video_credits`
    // in routers/videos.py); this client check just keeps the UI from
    // opening the Stripe purchase modal for an account that can't be
    // charged.
    //
    // Round 58.b (prod-resilience): re-check with the backend right
    // before deciding. Never trust stale localStorage / state alone —
    // production previously cached `is_internal=false` from before
    // the bypass envs were set, which made this gate fire incorrectly.
    let backendInternal = false;
    let backendBalance = videoCredits;
    try {
      const fresh = await videoAPI.getCredits();
      backendInternal = fresh?.data?.is_internal === true;
      backendBalance = fresh?.data?.video_credits ?? 0;
      setVideoCredits(backendBalance);
      if (backendInternal) setIsInternalAccount(true);
    } catch (e) {
      console.warn('[CreateVideo] balance refresh failed, falling back to cached state', e);
    }
    const allowBypass = backendInternal || isInternalAccount;
    console.info(
      `[CreateVideo] click — cachedCredits=${videoCredits} backendCredits=${backendBalance} `
      + `cachedInternal=${isInternalAccount} backendInternal=${backendInternal} `
      + `gate=${allowBypass ? 'BYPASS' : (backendBalance <= 0 ? 'OPEN_PURCHASE_MODAL' : 'PROCEED')}`
    );
    if (!allowBypass && backendBalance <= 0) {
      /* Phase G — no video credits modal */
      return;
    }

    // Single-submit guard — block re-entry while a job is already running.
    if (isCreatingVideo) {
      return;
    }

    setIsCreatingVideo(true);
    setVideoStatus('queued');
    setVideoUrl(null);
    setVideoError(null);

    // Refs so the polling loop and 5-minute timeout see live values
    // (avoids the stale-closure bug that caused "Video generation timed out"
    // to fire even when the job already completed / failed).
    const finishedRef = { current: false };
    const intervalRef = { current: null };
    const timeoutRef = { current: null };

    // PART 8 & 3: Use the currently selected hook
    const effectiveHook = getEffectiveHook();

    try {
      // Create video job with the EDITED draft values (not the raw model
      // output). Scene description is prepended to the video prompt so the
      // visual generator gets the most specific context first.
      const fullPrompt = cleanScene && !cleanPrompt.startsWith(cleanScene)
        ? `${cleanScene}. ${cleanPrompt}`
        : cleanPrompt;
      // Phase C (2026-05-27) — Clients always route to v2. Founders
      // can still flip the toggle to legacy (which 503s while
      // LEGACY_VIDEO_DISABLED=1). When `isInternalAccount=false` we
      // ignore the toggle and force v2 so clients get the canonical
      // engine path with billing/upcharge enforcement.
      const effectiveUseV2 = isInternalAccount ? useV2Engine : true;
      const response = effectiveUseV2
        ? await videoAPI.createVideoV2({
            // Phase 4.1 — minimal v2 payload. The v2 engine does the
            // same script/prompt validation + sentence-aware shorten
            // as the legacy backend, and ignores the legacy-specific
            // fields below (preflight tier).
            title: result.title || `${topic} Video`,
            script: cleanScript,
            video_prompt: fullPrompt.slice(0, PROMPT_MAX),
            hook: effectiveHook,
            cta: cleanCta || (result.execution_instructions?.cta_layout?.example_phrases?.[0] || null),
            target_duration_sec: 12,
            content_id: result?.content_id || null,
            // 2026-05-29 voice-preview gate: server REFUSES create-v2
            // (HTTP 400) unless these fields are present and the mp3
            // behind audio_url is playable (ffprobe duration > 0.5s).
            // Same shape Weekly Planner already sends.
            voice_preview: previewAudioUrl && previewDuration
              ? {
                  audio_url: previewAudioUrl,
                  audio_duration: previewDuration,
                  script_hash: previewedScript ? `cc-${previewedScript.length}` : null,
                  previewed_at: new Date().toISOString(),
                }
              : null,
          })
        : await videoAPI.createVideo({
        title: result.title || `${topic} Video`,
        script: cleanScript,
        video_prompt: fullPrompt.slice(0, PROMPT_MAX),  // hard-cap defence in depth
        // PART 3 & 8: Pass explicit hook for proper segmentation
        hook: effectiveHook,
        cta: cleanCta || (result.execution_instructions?.cta_layout?.example_phrases?.[0] || null),
        // Carry the extended-tier upcharge flag through. Server requires
        // this when preflight returns tier=extended; otherwise it returns
        // HTTP 402 extended_upcharge_required. Standard-tier jobs send
        // false (default) and the server ignores it.
        extended_upcharge_confirmed: Boolean(
          preflightData?.tier === 'extended' && extendedUpchargeConfirmed
        ),
        // Round 76 — Voice-first gate is now GLOBAL. Pass the
        // voice_preview from the shared voiceover-preview endpoint so
        // the backend gate at /api/videos/create passes. Same rule as
        // Weekly Planner — no Runway burn without a voice the user
        // has heard.
        voice_preview: previewAudioUrl && previewDuration
          ? {
              audio_url: previewAudioUrl,
              audio_duration: previewDuration,
              script_hash: previewedScript ? `cc-${previewedScript.length}` : null,
              previewed_at: new Date().toISOString(),
            }
          : null,
      });

      const jobId = response.data.job_id;
      setVideoJobId(jobId);

      // Refresh credit balance after starting job
      fetchVideoCredits();

      toast.success('Video queued — generating');

      // Poll for status
      intervalRef.current = setInterval(async () => {
        try {
          // Phase 4.1 — when v2 toggle is on, use the v2-status route
          // which returns the same {status, video_url, error_message,
          // error_code} shape so this poll logic doesn't fork.
          // Phase C (2026-05-27) — Clients always poll v2-status.
          const statusResponse = effectiveUseV2
            ? await videoAPI.getVideoStatusV2(jobId)
            : await videoAPI.getVideoStatus(jobId);
          const status = statusResponse.data.status;

          setVideoStatus(status);

          if (status === 'completed') {
            finishedRef.current = true;
            clearInterval(intervalRef.current);
            clearTimeout(timeoutRef.current);
            setVideoUrl(statusResponse.data.video_url);
            setIsCreatingVideo(false);
            toast.success('Your video is ready. Find your download in My Projects → Videos.', { duration: 6000 });
          } else if (status === 'failed') {
            finishedRef.current = true;
            clearInterval(intervalRef.current);
            clearTimeout(timeoutRef.current);

            const errorCode = statusResponse.data.error_code;
            const apiMsg = statusResponse.data.error_message;

            // Map known backend error_codes to honest user-facing messages
            // so the UI never shows "timed out" when something concrete failed.
            const friendly =
              errorCode === 'voiceover_failed'
                ? 'Voiceover service unavailable (payment / quota issue). No Runway credits were used.'
                : errorCode === 'runway_failed'
                ? `Runway video generation failed: ${apiMsg || 'unknown error'}`
                : errorCode === 'composition_failed'
                ? 'Video composition failed after Runway returned successfully.'
                : (apiMsg || 'Video generation failed');

            setVideoError(friendly);
            setIsCreatingVideo(false);
            toast.error(friendly);
          }
        } catch (error) {
          console.warn('Poll status error:', error?.response?.status, error?.message);
        }
      }, 5000); // Poll every 5 seconds

      // Wall-clock guard. Only fires if neither completion nor failure has
      // been observed (finishedRef stays false). Uses ref, NOT the captured
      // videoStatus variable, so it reflects the real current state.
      timeoutRef.current = setTimeout(() => {
        if (finishedRef.current) return;
        clearInterval(intervalRef.current);
        setVideoError(
          'Still processing after 5 minutes. The job may still complete in the background — check My Projects → Videos shortly.'
        );
        setIsCreatingVideo(false);
      }, 300000);

    } catch (error) {
      console.error('Error creating video:', error?.response?.status, error?.message);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || 'Failed to start video generation';
      // Server-side preflight mirrors (Round 9) — honest messages for
      // duration / upcharge rejections so the UI matches what the
      // backend is actually enforcing.
      let message;
      if (status === 400 && detail?.code === 'duration_exceeds_30s') {
        message = detail.message || 'This script is too long for PayGBot DFY video. Please shorten it or split it into multiple videos.';
      } else if (status === 402 && detail?.code === 'extended_upcharge_required') {
        message = detail.message || 'Extended video requires the +$2 upcharge. Please confirm above and try again.';
      } else {
        message = typeof detail === 'string' ? detail : (detail?.message || 'Failed to start video generation');
      }
      toast.error(message);
      setVideoStatus('failed');
      setVideoError(message);
      setIsCreatingVideo(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Sparkle size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Let's Create Content</h2>
                <p className="text-sm text-slate-400">I'll generate everything you need</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="close-modal-btn"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <div className="space-y-6">
                <BusinessMemoryBanner />

                {/* Round 108 — Mission continuation banner. Shown
                    when the customer arrived from Approve & Generate
                    → strategist → Create This Content. The topic
                    below is prefilled from the approved mission
                    draft; the customer can still edit before
                    generating. */}
                {isMissionMode && (
                  <div
                    className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-3 flex items-start space-x-3"
                    data-testid="mission-continuation-banner"
                  >
                    <Sparkle size={18} className="text-amber-300 flex-shrink-0 mt-0.5" weight="fill" />
                    <div className="flex-1 text-sm">
                      <p className="text-amber-200 text-[11px] uppercase tracking-wider font-semibold mb-0.5">
                        Continuing your mission
                      </p>
                      <p className="text-white font-medium leading-snug" data-testid="mission-continuation-title">
                        {missionHandoff.mission_title
                          || pickMissionField('title', 'hook')
                          || 'Today\u2019s Mission'}
                      </p>
                      {pickMissionField('hook') && (
                        <p className="text-slate-400 text-xs mt-1 line-clamp-2">
                          <span className="text-amber-200/70">Approved hook:</span>{' '}
                          {pickMissionField('hook')}
                        </p>
                      )}
                      {pickMissionField('cta') && (
                        <p className="text-slate-400 text-xs mt-0.5 line-clamp-1">
                          <span className="text-amber-200/70">Approved CTA:</span>{' '}
                          {pickMissionField('cta')}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Selected Viral Idea banner — explains why the topic is
                    pre-filled and lets the user swap for a different one. */}
                {selectedIdea && !isMissionMode && (
                  <div
                    className="bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-pink-500/10 border border-yellow-400/30 rounded-xl p-3 flex items-start space-x-3"
                    data-testid="selected-viral-idea-banner"
                  >
                    <Lightbulb size={18} className="text-yellow-300 flex-shrink-0 mt-0.5" weight="fill" />
                    <div className="flex-1 text-sm">
                      <p className="text-yellow-200 text-[11px] uppercase tracking-wider font-semibold mb-0.5">
                        Creating from selected viral idea
                      </p>
                      <p className="text-white font-medium leading-snug">
                        "{selectedIdea.topic || selectedIdea.title || selectedIdea.idea || String(selectedIdea)}"
                      </p>
                      {(selectedIdea.angle || selectedIdea.hook) && (
                        <p className="text-slate-400 text-xs mt-1">
                          Angle: {selectedIdea.angle || selectedIdea.hook}
                        </p>
                      )}
                    </div>
                    {onFindViralIdeas && (
                      <button
                        onClick={() => {
                          onClose();
                          onFindViralIdeas();
                        }}
                        className="text-yellow-200 hover:text-yellow-100 text-xs underline whitespace-nowrap"
                        data-testid="selected-viral-idea-swap"
                      >
                        Pick a different idea
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-testid="generate-topic-label">
                    {isMissionMode
                      ? 'Mission topic (already prefilled — edit if needed)'
                      : 'What topic should I create content about?'}
                  </label>
                  <textarea
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    data-testid="generate-topic-input"
                    placeholder="e.g., 3 side hustles you can start today with no money"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>

                {/* Smart Content Suggestions — hidden in mission
                    continuation mode; the approved topic wins. */}
                {!topic && !isMissionMode && suggestions.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Lightbulb size={16} className="text-yellow-400" weight="fill" />
                      <span className="text-sm text-yellow-400 font-medium">Here are some ideas for you</span>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {suggestions.slice(0, 5).map((suggestion, index) => (
                        <motion.button
                          key={suggestion.topic || suggestion.title || `sugg-${index}`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => setTopic(suggestion.topic)}
                          className="w-full text-left p-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-yellow-500/30 rounded-lg transition-all group"
                          data-testid={`suggestion-${index}`}
                        >
                          <p className="text-white text-sm font-medium group-hover:text-yellow-400 transition-colors">
                            {suggestion.title}
                          </p>
                          <p className="text-slate-400 text-xs mt-1 truncate">
                            {suggestion.hook}
                          </p>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {loadingSuggestions && !suggestions.length && (
                  <div className="flex items-center justify-center space-x-2 py-4">
                    <Spinner size={16} className="text-slate-400 animate-spin" />
                    <span className="text-sm text-slate-400">Finding ideas for you...</span>
                  </div>
                )}

                <button
                  onClick={handleGenerateWithStreaming}
                  disabled={isGenerating}
                  data-testid="generate-submit-btn"
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  {isGenerating ? (
                    <>
                      <Spinner size={20} className="animate-spin" />
                      <span>Creating your content...</span>
                    </>
                  ) : (
                    <>
                      <Play size={20} weight="fill" />
                      <span>Create My Content</span>
                    </>
                  )}
                </button>

                <p className="text-center text-sm text-slate-500">
                  I'll create hooks, a script, caption, and hashtags for you
                </p>
                
                {/* Free tier generation limit indicator */}
                {generationLimit && generationLimit.limit > 0 && (
                  <div className="text-center">
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      generationLimit.remaining <= 2 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {generationLimit.remaining} of {generationLimit.limit} free generations left today
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Generating Step with Streaming Progress */}
            {step === 'generating' && (
              <div className="py-4 space-y-6">
                {/* Progress Header */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Spinner size={32} className="text-white animate-spin" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">I'm Working On It</h3>
                  <p className="text-slate-400">{stageMessage || 'Creating something great for you...'}</p>
                </div>

                {/* Progress Steps */}
                <div className="space-y-3">
                  {stages.map((stageItem, index) => {
                    const currentIndex = getCurrentStageIndex();
                    const isComplete = index < currentIndex;
                    const isCurrent = stageItem.key === currentStage;
                    
                    return (
                      <motion.div
                        key={stageItem.key}
                        initial={{ opacity: 0.5, x: -10 }}
                        animate={{ 
                          opacity: isComplete || isCurrent ? 1 : 0.5, 
                          x: 0 
                        }}
                        className={`flex items-center space-x-4 p-3 rounded-lg transition-all duration-300 ${
                          isCurrent
                            ? 'bg-blue-500/20 border border-blue-500/50'
                            : isComplete
                            ? 'bg-emerald-500/10 border border-emerald-500/30'
                            : 'bg-white/5 border border-white/5'
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isCurrent
                              ? 'bg-blue-500'
                              : isComplete
                              ? 'bg-emerald-500'
                              : 'bg-slate-700'
                          }`}
                        >
                          {isComplete ? (
                            <CheckCircle size={16} weight="fill" className="text-white" />
                          ) : isCurrent ? (
                            <Spinner size={16} className="text-white animate-spin" />
                          ) : (
                            <span className="text-sm">{stageItem.icon}</span>
                          )}
                        </div>
                        <span
                          className={`font-medium ${
                            isCurrent
                              ? 'text-blue-400'
                              : isComplete
                              ? 'text-emerald-400'
                              : 'text-slate-500'
                          }`}
                        >
                          {stageItem.label}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Live Preview of Streaming Results */}
                {Object.keys(streamingResult).length > 0 && (
                  <div className="mt-6 space-y-4">
                    <div className="text-sm text-slate-400 uppercase tracking-wider font-medium">
                      Live Preview
                    </div>
                    
                    {streamingResult.title && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/5 border border-white/10 rounded-lg p-3"
                      >
                        <span className="text-xs text-slate-500 uppercase">Title</span>
                        <p className="text-white font-medium mt-1">{streamingResult.title}</p>
                      </motion.div>
                    )}
                    
                    {streamingResult.selected_hook && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3"
                      >
                        <span className="text-xs text-emerald-400 uppercase">Best Hook</span>
                        <p className="text-emerald-400 font-medium mt-1">{streamingResult.selected_hook}</p>
                      </motion.div>
                    )}
                    
                    {streamingResult.script && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/5 border border-white/10 rounded-lg p-3"
                      >
                        <span className="text-xs text-slate-500 uppercase">Script Preview</span>
                        <p className="text-slate-300 mt-1 text-sm line-clamp-3">{streamingResult.script}</p>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <div className="space-y-6">
                {/* Round 86 — Founder-only video engine selector,
                    hoisted to the TOP of the result view so it shows
                    regardless of `content_type` branch (social / reel /
                    video / product / funnel) the IIFE below renders.
                    Previously this selector was nested inside the
                    rich Review & Edit panel (block A) which only
                    renders for non-product/non-funnel content types,
                    so a founder generating a product/funnel asset (or
                    landing on the compact Create Your Video block at
                    the bottom of the modal) never saw the v2 toggle.
                    This top-of-modal placement guarantees the toggle
                    is reachable for every founder result render. */}
                {isInternalAccount && (
                  <div
                    data-testid="content-bot-v2-engine-panel-top"
                    className="px-3 py-2.5 rounded-lg border border-slate-700/60 bg-slate-900/40"
                  >
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                      Founder · video engine
                    </div>
                    <div
                      role="radiogroup"
                      aria-label="Video engine selector"
                      className="grid grid-cols-2 gap-2"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={!useV2Engine}
                        data-testid="content-bot-engine-legacy-btn-top"
                        onClick={() => setUseV2Engine(false)}
                        className={`text-xs px-3 py-2 rounded-md border transition-all text-left ${
                          !useV2Engine
                            ? 'border-fuchsia-500/60 bg-fuchsia-500/15 text-fuchsia-100 font-semibold'
                            : 'border-slate-700/60 bg-slate-800/40 text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        <div>Legacy (disabled)</div>
                        <div className="text-[10px] font-normal opacity-70">
                          Kill switch ON — 503s
                        </div>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={useV2Engine}
                        data-testid="content-bot-engine-v2-btn-top"
                        onClick={() => {
                          if (!v2EnvEnabled) {
                            toast.error(
                              'v2 canary disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend and restart, then refresh.',
                              { duration: 6000 },
                            );
                            return;
                          }
                          setUseV2Engine(true);
                        }}
                        disabled={!v2EnvEnabled && !v2EnvLoading}
                        title={
                          v2EnvLoading
                            ? 'Checking v2 status…'
                            : v2EnvEnabled
                              ? 'Run this video through Video Engine v2 (real ElevenLabs + Runway gen4.5).'
                              : 'v2 canary disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend and restart.'
                        }
                        className={`text-xs px-3 py-2 rounded-md border transition-all text-left ${
                          useV2Engine
                            ? 'border-amber-500/60 bg-amber-500/15 text-amber-100 font-semibold'
                            : v2EnvEnabled
                              ? 'border-slate-700/60 bg-slate-800/40 text-slate-300 hover:text-amber-200 hover:border-amber-500/40'
                              : 'border-slate-800/60 bg-slate-900/40 text-slate-600 cursor-not-allowed'
                        }`}
                      >
                        <div>v2 (canary)</div>
                        <div className="text-[10px] font-normal opacity-70">
                          {v2EnvLoading
                            ? 'checking…'
                            : v2EnvEnabled
                              ? 'Burns Runway credits'
                              : 'env flag OFF'}
                        </div>
                      </button>
                    </div>
                    <div
                      data-testid="content-bot-engine-diagnostic-top"
                      className="mt-2 text-[10px] font-mono text-slate-500 leading-tight"
                    >
                      founder_detected={String(isInternalAccount)}
                      {' · '}v2_env_enabled={
                        v2EnvLoading
                          ? 'loading'
                          : v2EnvError
                            ? `error(${v2EnvError})`
                            : String(v2EnvEnabled)
                      }
                      {' · '}selected_engine={useV2Engine ? 'v2' : 'legacy'}
                      {v2EnvStatus && (
                        <>
                          {' · '}elevenlabs={String(v2EnvStatus.elevenlabs_configured)}
                          {' · '}runway={String(v2EnvStatus.runway_configured)}
                        </>
                      )}
                    </div>
                  </div>
                )}
                {/* Universal Editable Preview Mode — fix the title /
                    hook / script / caption / hashtags before downloading
                    or scheduling. PATCH /api/content/{id} only updates
                    the editable whitelist; business_memory and the
                    video_prompt stay intact. */}
                {result.id && (
                  <EditablePreview
                    draftKey={`content-draft-${result.id}`}
                    originalValue={editableContent}
                    onSave={handleSaveContentEdits}
                    busy={savingContentEdits}
                    savedAt={result.last_edited_at || null}
                    previewSlot={({ value }) => (
                      <div className="space-y-2 text-sm text-slate-200">
                        {value.title && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Title</p>
                            <p className="text-white font-semibold">{value.title}</p>
                          </div>
                        )}
                        {value.selected_hook && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Hook</p>
                            <p className="text-emerald-300">{value.selected_hook}</p>
                          </div>
                        )}
                        {value.script && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Script</p>
                            <p className="whitespace-pre-wrap line-clamp-6">{value.script}</p>
                          </div>
                        )}
                        {value.caption && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Caption</p>
                            <p className="line-clamp-4">{value.caption}</p>
                          </div>
                        )}
                        {(value.hashtags || []).length > 0 && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Hashtags</p>
                            <p className="text-blue-300">{(value.hashtags || []).map(t => `#${t}`).join(' ')}</p>
                          </div>
                        )}
                      </div>
                    )}
                    editSlot={({ value, setValue }) => (
                      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Title</span>
                          <input
                            type="text"
                            value={value.title || ''}
                            onChange={(e) => setValue({ ...value, title: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-violet-400 focus:outline-none"
                            data-testid="content-edit-title"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Hook</span>
                          <input
                            type="text"
                            value={value.selected_hook || ''}
                            onChange={(e) => setValue({ ...value, selected_hook: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-violet-400 focus:outline-none"
                            data-testid="content-edit-hook"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Script</span>
                          <textarea
                            value={value.script || ''}
                            onChange={(e) => setValue({ ...value, script: e.target.value })}
                            rows={5}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-violet-400 focus:outline-none"
                            data-testid="content-edit-script"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Caption</span>
                          <textarea
                            value={value.caption || ''}
                            onChange={(e) => setValue({ ...value, caption: e.target.value })}
                            rows={4}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-violet-400 focus:outline-none"
                            data-testid="content-edit-caption"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Hashtags (space-separated)</span>
                          <input
                            type="text"
                            value={(value.hashtags || []).join(' ')}
                            onChange={(e) => setValue({ ...value, hashtags: e.target.value.split(/\s+/).map(s => s.replace(/^#/, '')).filter(Boolean) })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-violet-400 focus:outline-none"
                            data-testid="content-edit-hashtags"
                          />
                        </label>
                      </div>
                    )}
                  />
                )}

                {/* Content Type Aware Section */}
                {(() => {
                  const contentType = result.content_type || 'social';
                  const typeInstructions = result.content_type_instructions;
                  
                  // Content-type specific styling
                  const typeStyles = {
                    social: {
                      gradient: 'from-violet-500/20 to-fuchsia-500/20',
                      border: 'border-violet-500/30',
                      headerBg: 'bg-violet-500/10',
                      headerBorder: 'border-violet-500/20',
                      icon: VideoCamera,
                      iconColor: 'text-violet-400',
                      buttonGradient: 'from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700'
                    },
                    product: {
                      gradient: 'from-amber-500/20 to-orange-500/20',
                      border: 'border-amber-500/30',
                      headerBg: 'bg-amber-500/10',
                      headerBorder: 'border-amber-500/20',
                      icon: Package,
                      iconColor: 'text-amber-400',
                      buttonGradient: 'from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700'
                    },
                    funnel: {
                      gradient: 'from-blue-500/20 to-cyan-500/20',
                      border: 'border-blue-500/30',
                      headerBg: 'bg-blue-500/10',
                      headerBorder: 'border-blue-500/20',
                      icon: Funnel,
                      iconColor: 'text-blue-400',
                      buttonGradient: 'from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
                    }
                  };
                  
                  const style = typeStyles[contentType] || typeStyles.social;
                  const TypeIcon = style.icon;
                  const sectionTitle = typeInstructions?.section_title || 'Create Your Video';
                  const toolTip = typeInstructions?.tool_tip || result.execution_instructions?.tool_instruction || 'Copy and use with your preferred tool';
                  const instructions = typeInstructions?.instructions || [];
                  
                  // Review & Edit + Voiceover Preview workflow renders for
                  // ANY video-producing content type (social, reel, video,
                  // dfy_video, etc.) — gated only by `result.video_prompt`.
                  // Product / funnel have their own dedicated sections
                  // below which never include video_prompt.
                  if (result.video_prompt && contentType !== 'product' && contentType !== 'funnel') {
                    return (
                      <div className={`rounded-xl bg-gradient-to-br ${style.gradient} border ${style.border} overflow-hidden`}>
                        {/* Header */}
                        <div className={`p-4 border-b ${style.headerBorder} ${style.headerBg}`}>
                          <div className="flex items-center space-x-2">
                            <TypeIcon size={24} className={style.iconColor} weight="fill" />
                            <h3 className="text-lg font-semibold text-white">{sectionTitle}</h3>
                          </div>
                          <p className="text-slate-400 text-sm mt-1">{toolTip}</p>
                        </div>
                        
                        <div className="p-4 space-y-4">
                          {/* Video Prompt */}
                          <div>
                            <label className="text-xs font-medium text-violet-300 uppercase tracking-wider mb-2 block">Video Prompt</label>
                            <div className="p-3 bg-black/30 rounded-lg border border-white/10">
                              <p className="text-slate-200 text-sm leading-relaxed" data-testid="video-prompt-text">
                                {result.video_prompt}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2 mt-3">
                              <button
                                onClick={() => handleCopy(result.video_prompt, 'video-prompt')}
                                data-testid="copy-video-prompt-btn"
                                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center space-x-2 ${
                                  copiedField === 'video-prompt'
                                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                    : `bg-gradient-to-r ${style.buttonGradient} text-white`
                                }`}
                              >
                                {copiedField === 'video-prompt' ? (
                                  <>
                                    <CheckCircle size={16} />
                                    <span>Copied!</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy size={16} />
                                    <span>Copy Video Prompt</span>
                                  </>
                                )}
                              </button>
                              {result.video_prompt_alt && (
                                <button
                                  onClick={() => handleCopy(result.video_prompt_alt, 'video-prompt-alt')}
                                  data-testid="copy-video-prompt-alt-btn"
                                  className={`py-2.5 px-3 text-sm font-medium rounded-lg transition-all flex items-center space-x-1 ${
                                    copiedField === 'video-prompt-alt'
                                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                      : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/10'
                                  }`}
                                  title="Copy alternative prompt"
                                >
                                  {copiedField === 'video-prompt-alt' ? <CheckCircle size={16} /> : <ArrowClockwise size={16} />}
                                  <span>Alt</span>
                                </button>
                              )}
                            </div>
                            
                            {/* PART 7: Review & Edit panel — clients edit
                                video_prompt, script, CTA, and scene
                                description BEFORE Runway / ElevenLabs are
                                touched. Same UI for founder + client
                                workflows. */}
                            <div
                              className="mt-4 pt-4 border-t border-white/10 space-y-4"
                              data-testid="review-and-edit-panel"
                            >
                              <div className="rounded-lg bg-violet-500/10 border border-violet-500/30 p-3">
                                <p className="text-violet-200 text-sm font-semibold mb-1">
                                  Review &amp; Edit before generating
                                </p>
                                <p className="text-slate-400 text-xs leading-relaxed">
                                  No Runway or ElevenLabs is called until you click <span className="text-white font-medium">Create Video</span>.
                                  Character limit: <span className="text-white font-medium">{PROMPT_MAX}</span> for prompt, {SCRIPT_MAX} for script, {CTA_MAX} for CTA. Keep prompts clear and specific.
                                </p>
                              </div>

                              {/* Video prompt */}
                              <EditableField
                                testid="edit-video-prompt"
                                label="Video prompt"
                                value={videoPromptDraft}
                                onChange={setVideoPromptDraft}
                                max={PROMPT_MAX}
                                rows={4}
                                placeholder="Describe the scene exactly: subject, action, setting, mood..."
                              />

                              {/* Voiceover script */}
                              <EditableField
                                testid="edit-script"
                                label="Voiceover script"
                                value={scriptDraft}
                                onChange={setScriptDraft}
                                max={SCRIPT_MAX}
                                rows={4}
                                placeholder="The line ElevenLabs will speak."
                              />
                              {/* Round 52 — live word counter + 25–38 cap UX */}
                              <div
                                className={`flex items-center justify-between text-xs -mt-2 ${
                                  scriptWordsOverCap
                                    ? 'text-red-300'
                                    : scriptWordsUnderMin
                                    ? 'text-amber-300'
                                    : 'text-emerald-300'
                                }`}
                                data-testid="script-word-count"
                              >
                                <span>
                                  Words: <span className="font-semibold">{scriptWordCount}</span>{' '}
                                  / {SCRIPT_WORD_MAX} (target {SCRIPT_WORD_MIN}–{SCRIPT_WORD_MAX} ≈ 10–15 s)
                                </span>
                                {scriptWordsOverCap && (
                                  <button
                                    type="button"
                                    data-testid="auto-shorten-script-btn"
                                    disabled={shorteningScript}
                                    onClick={async () => {
                                      // Round 85 — SENTENCE-AWARE LLM rewrite.
                                      // Replaces the old `truncateToMaxWords`
                                      // word-slice that chopped the CTA off
                                      // the end. Backend route ensures the
                                      // rewrite preserves hook + key insight
                                      // + CTA inside the 14-22 word cap.
                                      setShorteningScript(true);
                                      try {
                                        const resp = await videoAPI.shortenScript({
                                          script: scriptDraft,
                                          title: result?.title || topic || 'PayGBot short video',
                                        });
                                        setScriptDraft(resp.shortened_script);
                                        // Clear any stale voice preview — the script changed.
                                        setPreviewedScript(null);
                                        setPreviewAudioUrl(null);
                                        setPreviewDuration(null);
                                        toast.success(
                                          `Rewritten ${resp.original_words} → ${resp.shortened_words} words (hook + insight + CTA preserved).`,
                                        );
                                      } catch (err) {
                                        const detailField = err?.response?.data?.detail;
                                        const msg = typeof detailField === 'string'
                                          ? detailField
                                          : (detailField?.message || err?.message || 'Could not rewrite script.');
                                        toast.error(msg);
                                      } finally {
                                        setShorteningScript(false);
                                      }
                                    }}
                                    className="px-2 py-0.5 rounded bg-red-500/20 border border-red-400/30 text-red-100 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {shorteningScript ? 'Rewriting…' : `Auto-shorten to ${SCRIPT_WORD_MAX}`}
                                  </button>
                                )}
                                {scriptWordsUnderMin && (
                                  <span className="text-amber-300/80">
                                    Add a few more words before submitting
                                  </span>
                                )}
                              </div>

                              {/* Voiceover preview — ElevenLabs only. NEVER calls Runway. */}
                              {(() => {
                                const cleanScript = (scriptDraft || '').trim();
                                const scriptChanged =
                                  previewedScript !== null && cleanScript !== previewedScript;
                                const canPreview =
                                  cleanScript.length > 0 &&
                                  cleanScript.length <= SCRIPT_MAX &&
                                  !previewLoading;
                                return (
                                  <div
                                    data-testid="voiceover-preview-panel"
                                    className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3 space-y-2"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <Microphone size={16} className="text-violet-300" />
                                        <span className="text-xs font-medium text-violet-200 uppercase tracking-wider">
                                          Voiceover preview
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        data-testid="preview-voiceover-btn"
                                        onClick={handleVoiceoverPreview}
                                        disabled={!canPreview}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${
                                          !canPreview
                                            ? 'bg-slate-700/60 text-slate-500 cursor-not-allowed border border-slate-600/40'
                                            : 'bg-violet-600 hover:bg-violet-500 text-white shadow-md shadow-violet-500/20'
                                        }`}
                                      >
                                        {previewLoading ? (
                                          <>
                                            <Spinner size={14} className="animate-spin" />
                                            <span>Generating...</span>
                                          </>
                                        ) : (
                                          <>
                                            <Play size={14} weight="fill" />
                                            <span>
                                              {previewAudioUrl ? 'Preview again' : 'Preview voiceover'}
                                            </span>
                                          </>
                                        )}
                                      </button>
                                    </div>
                                    <p className="text-[11px] text-slate-400 leading-relaxed">
                                      Hear the TTS voice before spending Runway credits. This calls ElevenLabs only —
                                      <span className="text-white"> zero Runway credits</span>.
                                    </p>
                                    {previewAudioUrl && !scriptChanged && (
                                      <div
                                        className="pt-1 space-y-1"
                                        data-testid="voiceover-preview-player-wrapper"
                                      >
                                        <audio
                                          key={previewAudioUrl}
                                          controls
                                          preload="auto"
                                          src={previewAudioUrl}
                                          data-testid="voiceover-preview-player"
                                          className="w-full h-10"
                                          onError={() => setPreviewPlaybackError(true)}
                                          onCanPlay={() => setPreviewPlaybackError(false)}
                                        >
                                          Your browser does not support audio playback.
                                        </audio>
                                        {previewPlaybackError ? (
                                          <p
                                            className="text-[11px] text-rose-400"
                                            data-testid="voiceover-preview-playback-error"
                                          >
                                            Preview audio could not be loaded. Tap Preview again to retry.
                                          </p>
                                        ) : (
                                          previewDuration && (
                                            <p
                                              className="text-[11px] text-emerald-400"
                                              data-testid="voiceover-preview-duration"
                                            >
                                              {previewDuration.toFixed(1)}s · ElevenLabs voiceover ready
                                            </p>
                                          )
                                        )}
                                      </div>
                                    )}
                                    {previewAudioUrl && scriptChanged && (
                                      <p
                                        className="text-[11px] text-amber-400"
                                        data-testid="voiceover-preview-stale"
                                      >
                                        Script changed — preview again to hear the new voiceover.
                                      </p>
                                    )}
                                    {previewError && !previewLoading && (
                                      <p
                                        className="text-[11px] text-rose-400"
                                        data-testid="voiceover-preview-error"
                                      >
                                        {previewError}
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Scene description */}
                              <EditableField
                                testid="edit-scene-description"
                                label="Scene description"
                                value={sceneDescriptionDraft}
                                onChange={setSceneDescriptionDraft}
                                max={SCENE_MAX}
                                rows={2}
                                placeholder="One-sentence visual setup. Prepended to the prompt."
                              />

                              {/* Call to action */}
                              <EditableField
                                testid="edit-cta"
                                label="Call to action"
                                value={ctaDraft}
                                onChange={setCtaDraft}
                                max={CTA_MAX}
                                rows={1}
                                placeholder='e.g. "Try PayGBot today."'
                              />
                            </div>

                            {/* ──────────────────────────────────────
                                Duration / pricing preflight banner —
                                server quote ONLY. Shows word count,
                                estimated seconds, tier, price, and —
                                when extended — a confirmation checkbox
                                the user MUST tick to unlock Create Video.
                                Blocked tier disables Create Video entirely.
                                No Runway / ElevenLabs calls here. */}
                            {(() => {
                              if (!(scriptDraft || '').trim()) return null;
                              if (preflightLoading && !preflightData) {
                                return (
                                  <div
                                    className="mt-4 rounded-lg border border-slate-600/40 bg-slate-800/60 px-3 py-2 text-xs text-slate-400 flex items-center gap-2"
                                    data-testid="preflight-loading"
                                  >
                                    <Spinner size={14} className="animate-spin" />
                                    <span>Estimating duration…</span>
                                  </div>
                                );
                              }
                              if (preflightError && !preflightData) {
                                return (
                                  <div
                                    className="mt-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300"
                                    data-testid="preflight-error"
                                  >
                                    {typeof preflightError === 'string' ? preflightError : 'Couldn\'t estimate duration right now.'}
                                  </div>
                                );
                              }
                              if (!preflightData) return null;

                              const tier = preflightData.tier;
                              const tierCfg = {
                                standard: {
                                  label: 'Standard',
                                  accent: 'emerald',
                                  bg: 'bg-emerald-500/10',
                                  border: 'border-emerald-500/30',
                                  dot: 'bg-emerald-400',
                                  titleClass: 'text-emerald-200',
                                },
                                extended: {
                                  label: 'Extended',
                                  accent: 'amber',
                                  bg: 'bg-amber-500/10',
                                  border: 'border-amber-500/30',
                                  dot: 'bg-amber-400',
                                  titleClass: 'text-amber-200',
                                },
                                blocked: {
                                  label: 'Too long',
                                  accent: 'rose',
                                  bg: 'bg-rose-500/10',
                                  border: 'border-rose-500/30',
                                  dot: 'bg-rose-400',
                                  titleClass: 'text-rose-200',
                                },
                              }[tier] || {
                                label: tier || 'Unknown',
                                bg: 'bg-slate-700/40',
                                border: 'border-slate-600/40',
                                dot: 'bg-slate-400',
                                titleClass: 'text-slate-200',
                              };
                              const seconds = preflightData.estimated_seconds;
                              const words = preflightData.word_count;

                              return (
                                <div
                                  className={`mt-4 rounded-lg border ${tierCfg.border} ${tierCfg.bg} p-3 space-y-2`}
                                  data-testid={`preflight-banner preflight-tier-${tier}`}
                                >
                                  <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className={`w-2 h-2 rounded-full ${tierCfg.dot}`} />
                                      <span className={`text-sm font-semibold ${tierCfg.titleClass}`}>
                                        {tierCfg.label} tier
                                      </span>
                                      <span className="text-slate-400 text-xs">
                                        · <span data-testid="preflight-word-count">{words}</span> words
                                        · ~<span data-testid="preflight-seconds">{Number(seconds).toFixed(1)}</span>s
                                      </span>
                                    </div>
                                    {preflightData.allowed && preflightData.price_display && preflightData.price_display !== '—' && (
                                      <span
                                        className={`text-sm font-semibold ${tier === 'extended' ? 'text-amber-300' : 'text-emerald-300'}`}
                                        data-testid="preflight-price"
                                      >
                                        {preflightData.price_display}
                                      </span>
                                    )}
                                  </div>

                                  {tier === 'standard' && (
                                    <p className="text-slate-400 text-xs" data-testid="preflight-standard-msg">
                                      Standard-duration video. Normal bundle pricing applies.
                                    </p>
                                  )}

                                  {tier === 'extended' && (
                                    <div className="space-y-2">
                                      <p className="text-amber-200 text-xs leading-relaxed" data-testid="preflight-extended-msg">
                                        Your video is longer than the standard 10–15 seconds. A <span className="font-semibold">$2 upgrade</span> is required per video.
                                      </p>
                                      <div className="rounded-md bg-black/30 border border-amber-500/20 px-3 py-2 text-xs text-amber-100 space-y-0.5">
                                        <div className="flex items-center justify-between">
                                          <span>1 extended video</span><span className="font-medium">$6.99</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <span>3 extended videos</span><span className="font-medium">$18.99</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <span>5 extended videos</span><span className="font-medium">$29.99</span>
                                        </div>
                                      </div>
                                      <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={extendedUpchargeConfirmed}
                                          onChange={(e) => setExtendedUpchargeConfirmed(e.target.checked)}
                                          data-testid="preflight-extended-confirm"
                                          className="mt-0.5 accent-amber-400"
                                        />
                                        <span className="text-amber-100 text-xs leading-snug">
                                          I confirm the <span className="font-semibold">+$2.00 per video</span> upcharge for this extended (16–30s) video.
                                        </span>
                                      </label>
                                    </div>
                                  )}

                                  {tier === 'blocked' && (
                                    <p className="text-rose-200 text-xs" data-testid="preflight-blocked-msg">
                                      {preflightData.message || 'This script is too long for PayGBot DFY video. Please shorten it or split it into multiple videos.'}
                                    </p>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Create Video Button — placed below review panel */}
                            <div className="mt-4 pt-4 border-t border-white/10">
                              {(() => {
                                const cleanPrompt = (videoPromptDraft || '').trim();
                                const cleanScript = (scriptDraft || '').trim();
                                const cleanCta = (ctaDraft || '').trim();
                                const cleanScene = (sceneDescriptionDraft || '').trim();
                                const promptOk = cleanPrompt.length >= 10 && cleanPrompt.length <= PROMPT_MAX;
                                const scriptOk = cleanScript.length > 0 && cleanScript.length <= SCRIPT_MAX;
                                const ctaOk = cleanCta.length <= CTA_MAX;
                                const sceneOk = cleanScene.length <= SCENE_MAX;
                                // Preflight gates — mirror the server-side 400/402 behaviour so
                                // the user sees the block in-app instead of after a rejected
                                // network round-trip.
                                const preflightBlocked = preflightData?.tier === 'blocked' || preflightData?.allowed === false;
                                const extendedUnconfirmed = preflightData?.tier === 'extended' && !extendedUpchargeConfirmed;
                                // Round 76 — Voice-first gate (GLOBAL).
                                // Mirrors backend /videos/create which now
                                // returns 400 voice_preview_required when
                                // the user clicks Create Video before
                                // previewing. Show the block in-app so we
                                // never spend a Runway credit on a script
                                // the user hasn't heard.
                                const scriptChanged =
                                  previewedScript !== null && cleanScript !== previewedScript;
                                const voicePreviewMissing =
                                  !previewAudioUrl || !previewDuration || scriptChanged;
                                const allOk = promptOk && scriptOk && ctaOk && sceneOk && !preflightBlocked && !extendedUnconfirmed && !voicePreviewMissing;
                                const disabled = isCreatingVideo || !allOk;
                                const tooltip = !promptOk
                                  ? 'Video prompt required (10-990 chars)'
                                  : !scriptOk
                                    ? 'Voiceover script required (1-1500 chars)'
                                    : voicePreviewMissing
                                      ? (scriptChanged
                                          ? 'Script changed — preview voiceover again before creating video.'
                                          : 'Preview voiceover first (voice-first is required everywhere).')
                                    : !ctaOk
                                      ? 'Call to action over 150 char limit'
                                      : !sceneOk
                                        ? 'Scene description over 990 char limit'
                                        : preflightBlocked
                                          ? (preflightData?.message || 'Script is too long for PayGBot DFY video.')
                                          : extendedUnconfirmed
                                            ? 'Confirm the +$2 extended-video upcharge above to continue.'
                                            : '';
                                return (
                                  <>
                                    {isInternalAccount && (
                                      <div
                                        data-testid="content-bot-v2-engine-panel"
                                        className="mb-3 px-3 py-2.5 rounded-lg border border-slate-700/60 bg-slate-900/40"
                                      >
                                        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                                          Founder · video engine
                                        </div>
                                        <div
                                          role="radiogroup"
                                          aria-label="Video engine selector"
                                          className="grid grid-cols-2 gap-2"
                                        >
                                          <button
                                            type="button"
                                            role="radio"
                                            aria-checked={!useV2Engine}
                                            data-testid="content-bot-engine-legacy-btn"
                                            onClick={() => setUseV2Engine(false)}
                                            className={`text-xs px-3 py-2 rounded-md border transition-all text-left ${
                                              !useV2Engine
                                                ? 'border-fuchsia-500/60 bg-fuchsia-500/15 text-fuchsia-100 font-semibold'
                                                : 'border-slate-700/60 bg-slate-800/40 text-slate-400 hover:text-slate-200'
                                            }`}
                                          >
                                            <div>Legacy (default)</div>
                                            <div className="text-[10px] font-normal opacity-70">Existing DFY pipeline</div>
                                          </button>
                                          <button
                                            type="button"
                                            role="radio"
                                            aria-checked={useV2Engine}
                                            data-testid="content-bot-engine-v2-btn"
                                            onClick={() => {
                                              if (!v2EnvEnabled) {
                                                toast.error(
                                                  'v2 canary disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend and restart, then refresh this page.',
                                                  { duration: 6000 }
                                                );
                                                return;
                                              }
                                              setUseV2Engine(true);
                                            }}
                                            disabled={!v2EnvEnabled && !v2EnvLoading}
                                            title={
                                              v2EnvLoading
                                                ? 'Checking v2 status…'
                                                : v2EnvEnabled
                                                  ? 'Run this video through the new Video Engine v2 (real ElevenLabs + Runway gen4.5).'
                                                  : 'v2 canary disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend and restart.'
                                            }
                                            className={`text-xs px-3 py-2 rounded-md border transition-all text-left ${
                                              useV2Engine
                                                ? 'border-amber-500/60 bg-amber-500/15 text-amber-100 font-semibold'
                                                : v2EnvEnabled
                                                  ? 'border-slate-700/60 bg-slate-800/40 text-slate-300 hover:text-amber-200 hover:border-amber-500/40'
                                                  : 'border-slate-800/60 bg-slate-900/40 text-slate-600 cursor-not-allowed'
                                            }`}
                                          >
                                            <div>v2 (canary)</div>
                                            <div className="text-[10px] font-normal opacity-70">
                                              {v2EnvLoading
                                                ? 'checking…'
                                                : v2EnvEnabled
                                                  ? 'Burns Runway credits'
                                                  : 'env flag OFF'}
                                            </div>
                                          </button>
                                        </div>
                                        <div
                                          data-testid="content-bot-engine-diagnostic"
                                          className="mt-2 text-[10px] font-mono text-slate-500 leading-tight"
                                        >
                                          founder_detected={String(isInternalAccount)}
                                          {' · '}v2_env_enabled={
                                            v2EnvLoading
                                              ? 'loading'
                                              : v2EnvError
                                                ? `error(${v2EnvError})`
                                                : String(v2EnvEnabled)
                                          }
                                          {' · '}selected_engine={useV2Engine ? 'v2' : 'legacy'}
                                          {v2EnvStatus && (
                                            <>
                                              {' · '}elevenlabs={String(v2EnvStatus.elevenlabs_configured)}
                                              {' · '}runway={String(v2EnvStatus.runway_configured)}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {/* Phase B (2026-05-27) — Hide the
                                        legacy "Create Video" / "Create
                                        this video for me" button from
                                        clients. Founder still sees it.
                                        Clients see the Copy Prompt
                                        action + tool_instruction footer
                                        so they can paste into their own
                                        AI video tool. Phase C will
                                        replace this guard with a v2
                                        billing-aware gate. */}
                                    {isInternalAccount && (
                                    <button
                                      onClick={handleCreateVideo}
                                      disabled={disabled}
                                      data-testid="create-video-btn"
                                      title={tooltip}
                                      className={`w-full py-3 text-base font-bold rounded-xl transition-all flex items-center justify-center space-x-2 ${
                                        isCreatingVideo
                                          ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                                          : !allOk
                                            ? 'bg-slate-700/60 text-slate-500 cursor-not-allowed border border-slate-600/40'
                                            : videoStatus === 'completed'
                                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                              : 'bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-700 hover:to-violet-700 text-white shadow-lg shadow-fuchsia-500/25'
                                      }`}
                                    >
                                      {isCreatingVideo ? (
                                        <>
                                          <Spinner size={20} className="animate-spin" />
                                          <span>{videoStatus === 'queued' ? 'Starting...' : 'Creating Video...'}</span>
                                        </>
                                      ) : videoStatus === 'completed' ? (
                                        <>
                                          <CheckCircle size={20} weight="fill" />
                                          <span>Video Ready!</span>
                                        </>
                                      ) : (
                                        <>
                                          <FilmSlate size={20} weight="fill" />
                                          <span>Create Video</span>
                                        </>
                                      )}
                                    </button>
                                    )}
                                    {!allOk && !isCreatingVideo && isInternalAccount && (
                                      <p
                                        className="mt-2 text-xs text-amber-400 text-center"
                                        data-testid="create-video-prompt-required"
                                      >
                                        {tooltip || 'Fill the required fields above before creating the video.'}
                                      </p>
                                    )}
                                  </>
                                );
                              })()}
                              
                              {/* Video Status Messages */}
                              {videoStatus === 'processing' && (
                                <p className="text-center text-violet-400 text-sm mt-2">
                                  Generating your video... This may take 2-3 minutes
                                </p>
                              )}
                              {videoError && (
                                <p className="text-center text-red-400 text-sm mt-2">{videoError}</p>
                              )}
                              {videoUrl && (
                                <a
                                  href={videoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center space-x-2 mt-3 text-emerald-400 hover:text-emerald-300 text-sm"
                                >
                                  <ArrowSquareOut size={16} />
                                  <span>Open Video</span>
                                </a>
                              )}
                            </div>
                          </div>
                          
                          {/* Execution Instructions - Collapsible */}
                          {result.execution_instructions && (
                            <div className="pt-3 border-t border-white/10">
                              <button
                                onClick={() => setShowExecutionDetails(!showExecutionDetails)}
                                className="w-full flex items-center justify-between text-sm text-violet-300 hover:text-violet-200 transition-colors"
                              >
                                <span className="flex items-center space-x-2">
                                  <Info size={16} />
                                  <span>Video Creation Guide</span>
                                </span>
                                <CaretDown size={16} className={`transform transition-transform ${showExecutionDetails ? 'rotate-180' : ''}`} />
                              </button>
                              
                              {showExecutionDetails && (
                                <div className="mt-4 space-y-4 text-sm">
                                  {/* Hook Layout */}
                                  {result.execution_instructions.hook_layout && (
                                    <div className="p-3 bg-black/20 rounded-lg border border-white/5">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <TextAlignLeft size={16} className="text-amber-400" />
                                        <span className="font-medium text-amber-400">Hook Layout</span>
                                      </div>
                                      <div className="space-y-1.5 text-slate-300 text-xs">
                                        <p><span className="text-slate-500">Placement:</span> {result.execution_instructions.hook_layout.placement}</p>
                                        <p><span className="text-slate-500">Timing:</span> {result.execution_instructions.hook_layout.timing}</p>
                                        <p><span className="text-slate-500">Format:</span> {result.execution_instructions.hook_layout.line_format}</p>
                                        <p><span className="text-slate-500">Style:</span> {result.execution_instructions.hook_layout.emphasis}</p>
                                        {result.execution_instructions.hook_layout.example_lines && (
                                          <div className="mt-2 p-2 bg-black/30 rounded border border-amber-500/20">
                                            <p className="text-amber-300/70 text-[10px] uppercase tracking-wider mb-1">Example Lines:</p>
                                            {result.execution_instructions.hook_layout.example_lines.map((line, i) => (
                                              <p key={`hook-line-${i}-${(line || '').slice(0, 12)}`} className="text-white font-medium">{line}</p>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Caption Layout */}
                                  {result.execution_instructions.caption_layout && (
                                    <div className="p-3 bg-black/20 rounded-lg border border-white/5">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <TextT size={16} className="text-blue-400" />
                                        <span className="font-medium text-blue-400">Caption Layout</span>
                                      </div>
                                      <div className="space-y-1.5 text-slate-300 text-xs">
                                        <p><span className="text-slate-500">Line Length:</span> {result.execution_instructions.caption_layout.line_length}</p>
                                        <p><span className="text-slate-500">Mobile Tip:</span> {result.execution_instructions.caption_layout.mobile_tip}</p>
                                        <p><span className="text-slate-500">Pacing:</span> {result.execution_instructions.caption_layout.pacing}</p>
                                        {result.execution_instructions.caption_layout.highlight_phrases?.length > 0 && (
                                          <div className="mt-2">
                                            <p className="text-slate-500 mb-1">Key Phrases to Highlight:</p>
                                            <div className="flex flex-wrap gap-1">
                                              {result.execution_instructions.caption_layout.highlight_phrases.map((phrase, i) => (
                                                <span key={`hl-${i}-${(phrase || '').slice(0, 12)}`} className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-[10px]">{phrase}</span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* CTA Layout */}
                                  {result.execution_instructions.cta_layout && (
                                    <div className="p-3 bg-black/20 rounded-lg border border-white/5">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <Megaphone size={16} className="text-green-400" />
                                        <span className="font-medium text-green-400">Call-to-Action</span>
                                      </div>
                                      <div className="space-y-1.5 text-slate-300 text-xs">
                                        <p><span className="text-slate-500">Placement:</span> {result.execution_instructions.cta_layout.placement}</p>
                                        <p><span className="text-slate-500">Timing:</span> {result.execution_instructions.cta_layout.timing}</p>
                                        <div className="mt-2">
                                          <p className="text-slate-500 mb-1">Example CTAs:</p>
                                          <div className="flex flex-wrap gap-1">
                                            {result.execution_instructions.cta_layout.example_phrases.map((phrase, i) => (
                                              <span key={`cta-${i}-${(phrase || '').slice(0, 12)}`} className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-[10px]">{phrase}</span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Delivery Style */}
                                  {result.execution_instructions.delivery_style && (
                                    <div className="p-3 bg-black/20 rounded-lg border border-white/5">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <Microphone size={16} className="text-pink-400" />
                                        <span className="font-medium text-pink-400">Delivery Style</span>
                                      </div>
                                      <div className="space-y-1.5 text-slate-300 text-xs">
                                        <p><span className="text-slate-500">Tone:</span> {result.execution_instructions.delivery_style.tone}</p>
                                        <p><span className="text-slate-500">Energy:</span> {result.execution_instructions.delivery_style.energy}</p>
                                        <p className="mt-2 p-2 bg-pink-500/10 border border-pink-500/20 rounded text-pink-200 italic">
                                          Tip: {result.execution_instructions.delivery_style.tip}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  // For product content
                  if (contentType === 'product') {
                    return (
                      <div className={`rounded-xl bg-gradient-to-br ${style.gradient} border ${style.border} overflow-hidden`}>
                        <div className={`p-4 border-b ${style.headerBorder} ${style.headerBg}`}>
                          <div className="flex items-center space-x-2">
                            <TypeIcon size={24} className={style.iconColor} weight="fill" />
                            <h3 className="text-lg font-semibold text-white">{sectionTitle}</h3>
                          </div>
                          <p className="text-slate-400 text-sm mt-1">{toolTip}</p>
                        </div>
                        
                        <div className="p-4 space-y-4">
                          {/* Instructions */}
                          <div className="space-y-2">
                            {instructions.map((instruction, i) => (
                              <div key={`inst-amber-${i}-${(instruction || '').slice(0, 16)}`} className="flex items-start space-x-2 text-sm">
                                <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                                <span className="text-slate-300">{instruction}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Script as outline */}
                          <div>
                            <label className="text-xs font-medium text-amber-300 uppercase tracking-wider mb-2 block">Product Outline</label>
                            <div className="p-3 bg-black/30 rounded-lg border border-white/10 max-h-40 overflow-y-auto">
                              <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">{result.script}</p>
                            </div>
                            <button
                              onClick={() => handleCopy(result.script, 'script')}
                              data-testid="copy-script-btn"
                              className={`w-full mt-3 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center space-x-2 ${
                                copiedField === 'script'
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                  : `bg-gradient-to-r ${style.buttonGradient} text-white`
                              }`}
                            >
                              {copiedField === 'script' ? (
                                <>
                                  <CheckCircle size={16} />
                                  <span>Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy size={16} />
                                  <span>Copy Outline</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // For funnel content
                  if (contentType === 'funnel') {
                    return (
                      <div className={`rounded-xl bg-gradient-to-br ${style.gradient} border ${style.border} overflow-hidden`}>
                        <div className={`p-4 border-b ${style.headerBorder} ${style.headerBg}`}>
                          <div className="flex items-center space-x-2">
                            <TypeIcon size={24} className={style.iconColor} weight="fill" />
                            <h3 className="text-lg font-semibold text-white">{sectionTitle}</h3>
                          </div>
                          <p className="text-slate-400 text-sm mt-1">{toolTip}</p>
                        </div>
                        
                        <div className="p-4 space-y-4">
                          {/* Instructions */}
                          <div className="space-y-2">
                            {instructions.map((instruction, i) => (
                              <div key={`inst-blue-${i}-${(instruction || '').slice(0, 16)}`} className="flex items-start space-x-2 text-sm">
                                <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                                <span className="text-slate-300">{instruction}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Script as setup text */}
                          <div>
                            <label className="text-xs font-medium text-blue-300 uppercase tracking-wider mb-2 block">Setup Text</label>
                            <div className="p-3 bg-black/30 rounded-lg border border-white/10 max-h-40 overflow-y-auto">
                              <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">{result.script}</p>
                            </div>
                            <button
                              onClick={() => handleCopy(result.script, 'script')}
                              data-testid="copy-script-btn"
                              className={`w-full mt-3 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center space-x-2 ${
                                copiedField === 'script'
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                  : `bg-gradient-to-r ${style.buttonGradient} text-white`
                              }`}
                            >
                              {copiedField === 'script' ? (
                                <>
                                  <CheckCircle size={16} />
                                  <span>Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy size={16} />
                                  <span>Copy Setup Text</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // Fallback for social without video prompt
                  return null;
                })()}

                {/* Quick Copy Actions */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => handleCopy(result.script, 'quick-script')}
                    data-testid="copy-script-btn"
                    className={`p-3 rounded-xl text-center transition-all ${
                      copiedField === 'quick-script'
                        ? 'bg-emerald-500/20 border-emerald-500/50'
                        : 'bg-white/10 hover:bg-white/20 border-white/10'
                    } border`}
                  >
                    {copiedField === 'quick-script' ? (
                      <CheckCircle size={24} className="text-emerald-400 mx-auto mb-1" />
                    ) : (
                      <Copy size={24} className="text-white mx-auto mb-1" />
                    )}
                    <span className="text-xs text-white font-medium">Copy Script</span>
                  </button>
                  <button
                    onClick={() => handleCopy(result.caption, 'quick-caption')}
                    data-testid="copy-caption-btn"
                    className={`p-3 rounded-xl text-center transition-all ${
                      copiedField === 'quick-caption'
                        ? 'bg-emerald-500/20 border-emerald-500/50'
                        : 'bg-white/10 hover:bg-white/20 border-white/10'
                    } border`}
                  >
                    {copiedField === 'quick-caption' ? (
                      <CheckCircle size={24} className="text-emerald-400 mx-auto mb-1" />
                    ) : (
                      <Copy size={24} className="text-white mx-auto mb-1" />
                    )}
                    <span className="text-xs text-white font-medium">Copy Caption</span>
                  </button>
                  <button
                    onClick={() => handleCopy(result.hashtags.map(t => `#${t}`).join(' '), 'quick-hashtags')}
                    data-testid="copy-hashtags-btn"
                    className={`p-3 rounded-xl text-center transition-all ${
                      copiedField === 'quick-hashtags'
                        ? 'bg-emerald-500/20 border-emerald-500/50'
                        : 'bg-white/10 hover:bg-white/20 border-white/10'
                    } border`}
                  >
                    {copiedField === 'quick-hashtags' ? (
                      <CheckCircle size={24} className="text-emerald-400 mx-auto mb-1" />
                    ) : (
                      <Copy size={24} className="text-white mx-auto mb-1" />
                    )}
                    <span className="text-xs text-white font-medium">Copy Hashtags</span>
                  </button>
                </div>

                {/* Learning Feedback Banner */}
                {result.learning_feedback?.has_learning && result.learning_feedback?.message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-3 rounded-lg flex items-center space-x-3 ${
                      result.learning_feedback.trend === 'improving'
                        ? 'bg-green-500/10 border border-green-500/30'
                        : result.learning_feedback.trend === 'needs_attention'
                          ? 'bg-amber-500/10 border border-amber-500/30'
                          : 'bg-blue-500/10 border border-blue-500/30'
                    }`}
                    data-testid="learning-feedback-banner"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      result.learning_feedback.trend === 'improving'
                        ? 'bg-green-500/20'
                        : result.learning_feedback.trend === 'needs_attention'
                          ? 'bg-amber-500/20'
                          : 'bg-blue-500/20'
                    }`}>
                      {result.learning_feedback.trend === 'improving' ? (
                        <Lightning size={16} className="text-green-400" weight="fill" />
                      ) : result.learning_feedback.trend === 'needs_attention' ? (
                        <Lightbulb size={16} className="text-amber-400" weight="fill" />
                      ) : (
                        <Sparkle size={16} className="text-blue-400" weight="fill" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        result.learning_feedback.trend === 'improving'
                          ? 'text-green-400'
                          : result.learning_feedback.trend === 'needs_attention'
                            ? 'text-amber-400'
                            : 'text-blue-400'
                      }`}>
                        {result.learning_feedback.message}
                      </p>
                      {result.learning_feedback.avg_score && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          Avg. content score: {result.learning_feedback.avg_score}
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Title */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Title</label>
                    <button
                      onClick={() => handleCopy(result.title, 'title')}
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'title' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-white text-lg font-semibold" data-testid="result-title">{result.title}</p>
                </div>

                {/* Selected Hook */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Selected Hook</label>
                    <button
                      onClick={() => handleCopy(getEffectiveHook(), 'hook')}
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'hook' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-emerald-400 font-medium bg-emerald-500/10 p-3 rounded-lg" data-testid="result-hook">
                    {getEffectiveHook()}
                  </p>
                </div>

                {/* PART 8: Hook Options - Now clickable for selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Hook Options <span className="text-xs text-slate-500">(tap to select)</span></label>
                  <div className="space-y-2">
                    {result.hook_options.map((hook, index) => {
                      const isSelected = hook === getEffectiveHook();
                      return (
                        <div
                          key={`hook-${index}-${(hook || '').slice(0, 20)}`}
                          onClick={() => handleSelectHook(hook)}
                          data-testid={`hook-option-${index}`}
                          className={`p-3 rounded-lg flex items-center justify-between cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-emerald-500/20 border-2 border-emerald-500/50 ring-2 ring-emerald-500/20'
                              : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            {isSelected && (
                              <CheckCircle size={16} className="text-emerald-400" weight="fill" />
                            )}
                            <span className={isSelected ? 'text-emerald-400 font-medium' : 'text-slate-300'}>
                              {hook}
                            </span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCopy(hook, `hook-${index}`); }}
                            className="text-slate-500 hover:text-white p-1"
                            title="Copy hook"
                          >
                            {copiedField === `hook-${index}` ? <CheckCircle size={14} /> : <Copy size={14} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Script */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Script</label>
                    <button
                      onClick={() => handleCopy(result.script, 'script')}
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'script' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-white bg-white/5 p-4 rounded-lg leading-relaxed" data-testid="result-script">
                    {result.script}
                  </p>
                </div>

                {/* Caption */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Caption</label>
                    <button
                      onClick={() => handleCopy(result.caption, 'caption')}
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'caption' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-slate-300 bg-white/5 p-4 rounded-lg" data-testid="result-caption">
                    {result.caption}
                  </p>
                </div>

                {/* Hashtags */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Hashtags</label>
                    <button
                      onClick={() => handleCopy(result.hashtags.map(t => `#${t}`).join(' '), 'hashtags')}
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'hashtags' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy All</span>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="result-hashtags">
                    {result.hashtags.map((tag, index) => (
                      <span
                        key={`tag-${index}-${tag}`}
                        onClick={() => handleCopy(`#${tag}`, `tag-${index}`)}
                        className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm cursor-pointer hover:bg-blue-500/30 transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Create Your Video - Video Prompt Section (Compact) */}
                {result.video_prompt && (
                  <div className="p-4 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <VideoCamera size={18} className="text-violet-400" weight="fill" />
                        <span className="text-sm font-medium text-violet-300">Create Your Video</span>
                      </div>
                    </div>
                    
                    {/* Primary Video Prompt */}
                    <div className="space-y-3">
                      <div className="p-3 bg-black/30 rounded-lg">
                        <p className="text-slate-300 text-sm leading-relaxed" data-testid="video-prompt-text-compact">
                          {result.video_prompt}
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleCopy(result.video_prompt, 'video-prompt')}
                          data-testid="copy-video-prompt-btn-compact"
                          className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center justify-center space-x-2 ${
                            copiedField === 'video-prompt'
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              : 'bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 border border-violet-500/20'
                          }`}
                        >
                          {copiedField === 'video-prompt' ? <CheckCircle size={16} /> : <Copy size={16} />}
                          <span>{copiedField === 'video-prompt' ? 'Copied!' : 'Copy Prompt'}</span>
                        </button>
                        
                        {result.video_prompt_alt && (
                          <button
                            onClick={() => handleCopy(result.video_prompt_alt, 'video-prompt-alt')}
                            data-testid="copy-video-prompt-alt-btn-compact"
                            className={`py-2.5 px-4 text-sm font-medium rounded-lg transition-all flex items-center space-x-2 ${
                              copiedField === 'video-prompt-alt'
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-white/10 text-slate-400 hover:bg-white/15 border border-white/10'
                            }`}
                            title="Copy alternative prompt"
                          >
                            {copiedField === 'video-prompt-alt' ? <CheckCircle size={16} /> : <ArrowClockwise size={16} />}
                            <span className="hidden sm:inline">Alt</span>
                          </button>
                        )}
                      </div>
                      
                      {/* Create Video Button */}
                      {/* Phase C (2026-05-27) — Open to clients. The
                          backend `_require_video_billing_ok` gate
                          enforces the 75-word/30s hard cap and the
                          extended-tier upcharge confirmation before
                          any ElevenLabs or Runway call. Founders
                          continue to see the Legacy/v2 engine toggle
                          (rendered above); clients automatically route
                          to v2 regardless of toggle state. */}
                      <div className="pt-2 border-t border-white/10">
                        {!videoStatus && !videoUrl && (
                          <button
                            onClick={handleCreateVideo}
                            disabled={isCreatingVideo}
                            data-testid="create-video-btn"
                            className="w-full py-3 bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-700 hover:to-violet-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                          >
                            <FilmSlate size={18} weight="fill" />
                            <span>Create this video for me</span>
                          </button>
                        )}
                        
                        {/* Video Generation Progress */}
                        {(videoStatus === 'queued' || videoStatus === 'processing') && (
                          <div className="p-4 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-xl">
                            <div className="flex items-center space-x-3">
                              <Spinner size={20} className="text-fuchsia-400 animate-spin" />
                              <div>
                                <p className="text-fuchsia-400 font-medium">
                                  {videoStatus === 'queued' ? 'Starting video generation...' : 'Generating your video...'}
                                </p>
                                <p className="text-slate-400 text-sm">This may take 1-3 minutes</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Video Ready */}
                        {videoStatus === 'completed' && videoUrl && (
                          <div className="space-y-3">
                            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                              <div className="flex items-center space-x-2 mb-2">
                                <CheckCircle size={20} className="text-emerald-400" weight="fill" />
                                <span className="text-emerald-400 font-semibold">Your video is ready!</span>
                              </div>
                              <p className="text-slate-400 text-sm">Video created with captions overlay</p>
                            </div>
                            <div className="flex space-x-2">
                              <a
                                href={videoUrl}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 py-2.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 font-medium rounded-lg flex items-center justify-center space-x-2"
                              >
                                <Play size={16} weight="fill" />
                                <span>Download Video</span>
                              </a>
                              <button
                                onClick={() => onAction?.('my-creations')}
                                className="flex-1 py-2.5 bg-white/10 text-slate-300 hover:bg-white/20 font-medium rounded-lg flex items-center justify-center space-x-2"
                              >
                                <span>View in My Creations</span>
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Video Failed */}
                        {videoStatus === 'failed' && (
                          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                            <p className="text-red-400 font-medium">Video generation failed</p>
                            <p className="text-slate-400 text-sm">{videoError || 'Please try again'}</p>
                            <button
                              onClick={handleCreateVideo}
                              className="mt-2 px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg text-sm"
                            >
                              Try Again
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Execution Guide - Compact Version */}
                      {result.execution_instructions && (
                        <div className="pt-2 border-t border-white/5">
                          <div className="grid grid-cols-2 gap-2 text-[10px]">
                            {result.execution_instructions.hook_layout && (
                              <div className="p-2 bg-black/20 rounded">
                                <span className="text-amber-400 font-medium">Hook:</span>
                                <span className="text-slate-400 ml-1">{result.execution_instructions.hook_layout.timing}</span>
                              </div>
                            )}
                            {result.execution_instructions.cta_layout && (
                              <div className="p-2 bg-black/20 rounded">
                                <span className="text-green-400 font-medium">CTA:</span>
                                <span className="text-slate-400 ml-1">{result.execution_instructions.cta_layout.timing}</span>
                              </div>
                            )}
                            {result.execution_instructions.delivery_style && (
                              <div className="col-span-2 p-2 bg-black/20 rounded">
                                <span className="text-pink-400 font-medium">Tone:</span>
                                <span className="text-slate-400 ml-1">{result.execution_instructions.delivery_style.tone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-slate-500 text-center">
                        {result.execution_instructions?.tool_instruction || "Paste into SeaDance, Runway, or any AI video tool"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions - Simplified with clear next step */}
                <div className="space-y-3 pt-4">
                  {/* YOUR NEXT STEP - Always visible (PART 5: Force Next Step Logic) */}
                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/40 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Lightning size={16} className="text-yellow-400" weight="fill" />
                      <span className="text-white text-xs font-bold uppercase tracking-wide">Your Next Step</span>
                    </div>
                    <p className="text-green-300 font-semibold mb-2">Copy the video prompt above and create your video</p>
                    <p className="text-slate-400 text-sm">Paste into SeaDance, Runway, CapCut, or any AI video tool → Post to social media → Done!</p>
                  </div>
                  
                  {/* Tools to Build This */}
                  <ToolLinks outputType="content" compact />

                  {/* Regenerate + Find Viral Ideas quick actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleNewContent}
                      data-testid="generate-new-btn"
                      className="flex-1 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-xl transition-colors"
                    >
                      Create Another
                    </button>
                    <button
                      onClick={handleRegenerate}
                      disabled={isGenerating}
                      data-testid="regenerate-btn"
                      className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-xl transition-colors flex items-center space-x-2 disabled:opacity-50"
                    >
                      <ArrowClockwise size={16} />
                      <span>Regenerate</span>
                    </button>
                    <button
                      onClick={() => {
                        handleClose();
                        if (onFindViralIdeas) onFindViralIdeas();
                        else if (onAction) onAction('viral_ideas');
                      }}
                      data-testid="find-viral-ideas-btn"
                      className="px-4 py-2.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 border border-purple-400/30 text-purple-200 text-sm font-medium rounded-xl transition-colors flex items-center space-x-2"
                    >
                      <Lightbulb size={16} weight="fill" />
                      <span>Find Viral Ideas</span>
                    </button>
                  </div>

                  {/* Next Step Guide — continuous loop; after content → Plan My Week */}
                  <NextStepGuide
                    currentStep="content"
                    onPick={(stepKey) => {
                      handleClose();
                      if (stepKey === 'growth' && onPlanMyWeek) onPlanMyWeek();
                      else if (onPickStep) onPickStep(stepKey);
                      else if (onAction) onAction(stepKey);
                    }}
                    onDone={handleClose}
                  />
                </div>

                {/* What's Next Section - Hidden by default, toggle to show more options */}
                {showWhatsNext && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 pt-4 border-t border-white/10"
                  >
                    <WhatsNextContent onAction={handleWhatsNextAction} onClose={handleClose} />
                  </motion.div>
                )}
                
                {/* More options toggle */}
                <button
                  onClick={() => setShowWhatsNext(!showWhatsNext)}
                  className="w-full mt-2 py-2 text-sm text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showWhatsNext ? 'Hide options' : 'Want to do something else? →'}
                </button>
                
                {/* Generation Limit Indicator for Free Users */}
                {generationLimit && generationLimit.limit > 0 && (
                  <div className="mt-2 text-center text-xs text-slate-500">
                    {generationLimit.remaining} of {generationLimit.limit} free generations remaining today
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
      
      {/* Free Limit Modal */}
      <FreeLimitModal
        isOpen={showFreeLimitModal}
        onClose={() => setShowFreeLimitModal(false)}
        promptData={freeLimitPrompt}
        onBuildOffer={() => {
          handleClose();
          if (onAction) onAction('buildOffer');
        }}
      />
      
      {/* PART 5: Video Credits Purchase Modal */}
    </AnimatePresence>
  );
};

export default GenerateContentModal;
